// components/AdminManagement.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Plus, Trash2, ArrowLeft, UserPlus, Mail, Lock, 
  RefreshCw, Shield, UserCog, CheckCircle, XCircle 
} from 'lucide-react';
import { useAdmins } from '../hooks/useAdmins';

const AdminManagement = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const navigate = useNavigate();
  const { admins, loading, addAdmin, deleteAdmin, fetchAdmins } = useAdmins();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.username.trim()) {
      toast.error('Username is required');
      return;
    }
    if (formData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters!');
      return;
    }
    
    try {
      await addAdmin({
        username: formData.username,
        email: formData.email,
        password: formData.password
      });
      
      setShowForm(false);
      setFormData({ username: '', email: '', password: '', confirmPassword: '' });
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      await deleteAdmin(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ username: '', email: '', password: '', confirmPassword: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:text-gray-200 transition flex items-center"
            >
              <ArrowLeft className="inline-block mr-2" size={20} />
              Back
            </button>
            <h1 className="text-2xl font-bold">👑 Manage Admins</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAdmins}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md transition flex items-center"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition flex items-center"
            >
              <UserPlus className="mr-2" size={18} />
              Add Admin
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {/* Add Admin Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6 border-l-4 border-indigo-500">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <UserPlus className="mr-2 text-indigo-600" size={20} />
              Add New Admin
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Username */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <UserCog size={16} className="text-indigo-600" />
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter username (min 3 chars)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                  minLength={3}
                />
                <p className="text-xs text-gray-400 mt-1">Unique username for admin login</p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Mail size={16} className="text-indigo-600" />
                  Email
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
                <p className="text-xs text-gray-400 mt-1">Optional: Admin email address</p>
              </div>

              {/* Password */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Lock size={16} className="text-indigo-600" />
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  placeholder="Enter password (min 6 chars)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-400 mt-1">Password must be at least 6 characters</p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Shield size={16} className="text-indigo-600" />
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  placeholder="Confirm password"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-800"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Re-enter password to confirm</p>
              </div>

              <div className="md:col-span-2 flex gap-4">
                <button 
                  type="submit" 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <UserPlus className="mr-2" size={18} />
                  Add Admin
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <XCircle className="mr-2" size={18} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Admins Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">#</th>
                  <th className="px-4 py-3 text-left text-sm">👤 Username</th>
                  <th className="px-4 py-3 text-left text-sm">📧 Email</th>
                  <th className="px-4 py-3 text-left text-sm">👑 Role</th>
                  <th className="px-4 py-3 text-left text-sm">📅 Created</th>
                  <th className="px-4 py-3 text-left text-sm">⚡ Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No other admins found. You are the only admin!
                    </td>
                  </tr>
                ) : (
                  admins.map((admin, index) => (
                    <tr key={admin.id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-800 font-medium flex items-center gap-2">
                        <UserCog size={16} className="text-indigo-600" />
                        {admin.username}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{admin.email || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 flex items-center gap-1 w-fit">
                          <Shield size={14} />
                          Admin
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(admin.id)}
                          className="text-red-600 hover:text-red-800 transition p-1 hover:bg-red-50 rounded"
                          title="Delete Admin"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;